import { foldText, tokenize } from '../text/fold';

export interface PreparedQuery {
  folded: string;
  tokens: string[];
  /** Unique trigram keys of all tokens with 3+ characters. */
  trigrams: number[];
}

/** Packs three UTF-16 code units into one exact numeric key (< 2^48). */
export function trigramKey(text: string, index: number): number {
  return text.charCodeAt(index) * 4294967296 + text.charCodeAt(index + 1) * 65536 + text.charCodeAt(index + 2);
}

/** Unique, sorted trigram keys of a folded string, computed per token. */
export function trigramsOf(folded: string): number[] {
  const keys: number[] = [];
  for (const token of tokenize(folded)) {
    for (let i = 0; i + 3 <= token.length; i++) keys.push(trigramKey(token, i));
  }
  return uniqueSorted(keys);
}

/** Queries shorter than this many folded characters match nothing. */
export const MIN_QUERY_CHARS = 2;

/**
 * Folds and tokenizes a query. Returns `null` for input that cannot produce
 * useful results (blank, or a single character that would match half the
 * catalog).
 */
export function prepareQuery(raw: string): PreparedQuery | null {
  const folded = foldText(raw);
  if (folded.replace(/ /g, '').length < MIN_QUERY_CHARS) return null;
  return { folded, tokens: tokenize(folded), trigrams: trigramsOf(folded) };
}

/** Key of a token's first two code units, used for two-letter word-start lookups. */
export function wordPrefixKey(token: string): number {
  return token.charCodeAt(0) * 65536 + token.charCodeAt(1);
}

/** Unique, sorted two-letter word-start keys of a folded string. */
export function wordPrefixesOf(folded: string): number[] {
  const keys: number[] = [];
  for (const token of tokenize(folded)) {
    if (token.length >= 2) keys.push(wordPrefixKey(token));
  }
  return uniqueSorted(keys);
}

export function uniqueSorted(values: number[]): number[] {
  if (values.length < 2) return values;
  values.sort((a, b) => a - b);
  let write = 1;
  for (let read = 1; read < values.length; read++) {
    if (values[read] !== values[write - 1]) values[write++] = values[read] as number;
  }
  values.length = write;
  return values;
}
